<template>
  <div>
    <!-- Backdrop with blur -->
    <div class="modal-backdrop-blur"></div>

    <div class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <!-- Header -->
          <!-- Header -->
          <div
            class="modal-header d-flex justify-content-between align-items-center"
          >
            <div class="d-flex align-items-center">
              <p class="modal-title mb-0 me-3">Leads Activity Details</p>

              <!-- Download button just beside title -->
              <button
                type="button"
                class="btn btn-sm btn-outline-primary"
                @click="downloadReport"
              >
                <i class="bi bi-download"></i> Download
              </button>
            </div>

            <!-- Close button on the far right -->
            <button
              type="button"
              class="btn-close"
              @click="closeModal"
            ></button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <div v-if="employee && employee.leads.length">
              <div class="table-responsive">
                <table class="table table-hover align-middle corporate-table">
                  <thead>
                    <tr>
                      <th>Organization Name</th>
                      <th>Activity</th>
                      <th>Priority</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(lead, leadIndex) in employee.leads"
                      :key="leadIndex"
                    >
                      <!-- Org -->
                      <td>
                        {{ lead.organization_name || `Lead ${leadIndex + 1}` }}
                      </td>

                      <!-- Activity -->
                      <td>
                        <div v-if="hasActivity(lead)">
                          <ul class="list-unstyled mb-0">
                            <template v-if="lead.log_activity">
                              <li
                                v-for="(log, i) in lead.log_activity.split(
                                  '## '
                                )"
                                :key="'log-' + i"
                              >
                                <strong>Log:</strong> {{ log }}
                              </li>
                            </template>

                            <template v-if="lead.task_details">
                              <li
                                v-for="(task, i) in lead.task_details.split(
                                  '## '
                                )"
                                :key="'task-' + i"
                              >
                                <strong>Task:</strong> {{ task }}
                              </li>
                            </template>

                            <template v-if="lead.notes">
                              <li
                                v-for="(note, i) in lead.notes.split('## ')"
                                :key="'note-' + i"
                              >
                                <strong>Note:</strong> {{ note }}
                              </li>
                            </template>

                            <template v-if="lead.sms_description">
                              <li
                                v-for="(sms, i) in lead.sms_description.split(
                                  '## '
                                )"
                                :key="'sms-' + i"
                              >
                                <strong>SMS:</strong> {{ sms }}
                              </li>
                            </template>

                            <template v-if="lead.email_subject">
                              <li
                                v-for="(email, i) in lead.email_subject.split(
                                  '## '
                                )"
                                :key="'email-' + i"
                              >
                                <strong>Email:</strong> {{ email }}
                              </li>
                            </template>

                            <template v-if="lead.file_description">
                              <li
                                v-for="(file, i) in lead.file_description.split(
                                  '## '
                                )"
                                :key="'file-' + i"
                              >
                                <strong>File:</strong> {{ file }}
                              </li>
                            </template>
                          </ul>
                        </div>

                        <p v-else class="no-activity">
                          This lead has no activity.
                        </p>
                      </td>

                      <!-- Priority -->
                      <td>
                        <span
                          :class="[
                            'badge',
                            lead.priority === 'Very Good'
                              ? 'bg-teal'
                              : lead.priority === 'Good'
                              ? 'bg-success'
                              : lead.priority === 'Low'
                              ? 'bg-warning text-dark'
                              : lead.priority === 'Very Low'
                              ? 'bg-danger'
                              : 'bg-secondary',
                          ]"
                        >
                          {{ lead.priority || "N/A" }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div v-else>
              <p>No leads available.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
export default {
  props: {
    employee: Object,
  },
  methods: {
    hasActivity(lead) {
      return (
        lead.log_activity ||
        lead.task_details ||
        lead.notes ||
        lead.sms_description ||
        lead.email_subject ||
        lead.file_description
      );
    },
    closeModal() {
      document.body.classList.remove("modal-open");
      this.$emit("close");
    },
    downloadReport() {
      const rows = [];
      // Headers
      rows.push(["Organization Name", "Activity", "Priority"]);

      this.employee.leads.forEach((lead, index) => {
        const org = lead.organization_name || `Lead ${index + 1}`;

        const activities = [];

        if (this.hasActivity(lead)) {
          const pushSplitActivity = (field, label) => {
            if (lead[field]) {
              lead[field]
                .split(",")
                .map((item) => item.trim())
                .forEach((item) => {
                  if (item) activities.push(`${label}: ${item}`);
                });
            }
          };

          pushSplitActivity("log_activity", "Log");
          pushSplitActivity("task_details", "Task");
          pushSplitActivity("notes", "Note");
          pushSplitActivity("sms_description", "SMS");
          pushSplitActivity("email_subject", "Email");
          pushSplitActivity("file_description", "File");
        } else {
          activities.push("This lead has no activity.");
        }

        const activity = activities.join("\r\n");
        const priority = lead.priority || "N/A";

        rows.push([org, activity, priority]);
      });

      // Create worksheet from rows
      const ws = XLSX.utils.aoa_to_sheet(rows);

      // Apply styles: wrap text + vertical top alignment to all cells
      Object.keys(ws).forEach((cell) => {
        if (cell[0] === "!") return; // skip metadata keys
        ws[cell].t = "s"; // treat as string
        ws[cell].s = {
          alignment: { vertical: "top", wrapText: true },
        };
      });

      // Auto row height based on number of lines in cells
      const rowHeights = rows.map((row) => {
        const maxLines = Math.max(
          ...row.map((cell) =>
            typeof cell === "string" ? cell.split(/\r\n/).length : 1
          )
        );
        return { hpt: 15 * maxLines + 5 }; // 15pt per line + 5pt padding
      });
      ws["!rows"] = rowHeights;

      // Auto column widths (wch = characters width)
      ws["!cols"] = [
        { wch: 30 }, // Organization Name - wide enough for names
        { wch: 70 }, // Activity - wider to accommodate multiline text
        { wch: 12 }, // Priority - smaller width
      ];

      // Create workbook and add the worksheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Leads Report");

      // Export as XLSX file
      XLSX.writeFile(wb, "LeadsActivityReport.xlsx");
    },
  },
  mounted() {
    // Prevent background scroll
    document.body.classList.add("modal-open");
  },
  beforeUnmount() {
    document.body.classList.remove("modal-open");
  },
};
</script>

<style scoped>
/* Backdrop with blur */
.modal-backdrop-blur {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(5px);
  z-index: 1040;
}

/* Prevent background scroll */
:global(body.modal-open) {
  overflow: hidden;
}

/* Modal style */
.modal-content {
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 1050;
}
.modal-header {
  background: #ececf1;
  color: #000;
  border-radius: 10px 10px 0 0;
}
.modal-body {
  max-height: 600px;
  overflow-y: auto;
  padding: 0;
}

/* Corporate table look */
.corporate-table th {
  position: sticky;
  top: 0;
  background: #37474f;
  color: #fff;
  font-weight: 300;
  z-index: 2;
  padding: 12px;
}
.corporate-table td {
  vertical-align: top;
  padding: 10px;
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal; /* allows auto line breaks */
  max-width: 400px; /* optional: limit column width */
}

.corporate-table tbody tr:hover td {
  background: #f8f9fa;
}

/* Badge styling */
.badge {
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 12px;
}

.bg-teal {
  background-color: #009688 !important;
  color: #fff;
}
</style>
